<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            `${isAdd ? $t("actions.add") : $t("actions.edit")} ${$tc(
              "type_alarm_category.title",
              1
            )}`
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- name -->
          <validation-provider
            #default="validationContext"
            :name="$t('type_alarm_category.name')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('type_alarm_category.name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                data-cy="name"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- type alarm -->
          <validation-provider
            #default="validationContext"
            :name="$t('type_alarm_category.type_alarm')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('type_alarm_category.type_alarm')"
              label-for="type"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_type_alarms"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="typeAlarmOptions"
                multiple
                :clearable="false"
                input-id="id_type_alarm"
                data-cy="alarmType"
                :close-on-select="false"
                :reduce="(val) => val.value"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
              data-cy="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed, getCurrentInstance, onMounted } from "@vue/composition-api";
import { required, alphaNum, positive } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { ICON_NAMES } from "@/assets/svg/CustomIcons";
import DurationHmInput from "@/components/BuildRiskForm/DurationHmInput.vue";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormInvalidFeedback,
    BButton,
    BFormCheckbox,
    DurationHmInput,
    vSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      positive,
      durationError: null,
    };
  },
  setup(props, { emit }) {
    const blankData = {
      id: null,
      name: "",
      id_type_alarms: [],
    };

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      if (instance?.proxy) instance.proxy.durationError = null;
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    const instance = getCurrentInstance();

    const toast = useToast();

    const onSubmit = () => {
      let dispatch;

      if (props.isAdd) {
        dispatch = store.dispatch("type_alarm_category/add", itemData.value);
      } else {
        const data = {
          id: itemData.value.id,
          params: itemData.value,
        };

        dispatch = store.dispatch("type_alarm_category/edit", data);
      }

      dispatch
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.message,
              icon: "CheckIcon",
              variant: "success",
            },
          });
          emit("refetch-data");
          emit("update:is-sidebar-active", false);
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const loadData = () => {
      if (props.isAdd) {
        resetData();
        return;
      }

      itemData.value = {
        id: props.itemEdit.id,
        name: props.itemEdit.name,
        id_type_alarms:
          props.itemEdit.type_alarms?.map((typeAlarm) => typeAlarm.id) || [],
      };
    };

    onMounted(() => {
      store.dispatch("type_alarm/getAll", { max: 1000, enabled: true });
    });

    const typeAlarmOptions = computed(() => store.getters["type_alarm/getSelectedItems"]);

    return {
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      typeAlarmOptions,
    };
  },
};
</script>

<style scoped></style>
